import { useState } from 'react';
import { Link } from 'react-router-dom';
import { assetUrl } from '../lib/postAssets.js';

/**
 * Hover previews for post rows/cards (Inbox + Posts list):
 *   <PostThumb>  — first-slide thumbnail; hovering reveals the remaining
 *                  slides in a popover. Slides load lazily (first
 *                  mouseenter), so a 50-row list doesn't fetch every image.
 *   <TextHover>  — wraps any element; hovering shows the full text details
 *                  (title, caption / story body, hashtags).
 * Both rely on the list API's image_assets / story_text fields.
 */

export function PostThumb({ post, linkTo, className = '' }) {
  const [revealed, setRevealed] = useState(false);
  const assets = Array.isArray(post.image_assets) ? post.image_assets : [];
  if (!assets.length) return null;

  const first = assets[0];
  const rest = assets.slice(1);
  const img = (
    <img
      src={assetUrl(post.id, first)}
      alt={post.title || 'Post preview'}
      loading="lazy"
    />
  );

  return (
    <span
      className={`hover-pop ${className}`}
      onMouseEnter={() => setRevealed(true)}
    >
      {linkTo
        ? <Link to={linkTo} className="thumb-link" title="Open post">{img}</Link>
        : img}
      {revealed && rest.length > 0 && (
        <span className="hover-pop-panel hover-pop-imgs">
          {rest.map((a) => (
            <img key={a.ordinal} src={assetUrl(post.id, a)} alt={`Slide ${a.ordinal}`} />
          ))}
        </span>
      )}
    </span>
  );
}

export function TextHover({ post, children, className = '' }) {
  const caption = post.caption || post.story_text || '';
  const hashtags = Array.isArray(post.hashtags) ? post.hashtags : [];
  const hasDetails = Boolean(post.title || caption || hashtags.length);
  if (!hasDetails) return children;

  return (
    <span className={`hover-pop ${className}`}>
      {children}
      <span className="hover-pop-panel hover-pop-text">
        {post.title && <strong>{post.title}</strong>}
        {caption && <span className="hover-pop-caption">{caption}</span>}
        {hashtags.length > 0 && (
          <span className="hover-pop-tags">{hashtags.map((h) => (h.startsWith('#') ? h : `#${h}`)).join(' ')}</span>
        )}
      </span>
    </span>
  );
}
